/**
 * @brief 控制台专用 MQTT 客户端
 * @note 通过 MQTT-over-WebSocket 直连 broker
 */

import mqtt, { MqttClient as MqttJsClient } from 'mqtt';
import {
  MQTT_BROKER_URL,
  MQTT_CLIENT_ID_PREFIX,
  MQTT_PASSWORD,
  MQTT_TOPIC_CMD,
  MQTT_TOPIC_STATE,
  MQTT_USERNAME,
} from '@/config';
import logger from '@/utils/logger';

export type ConnectState = 'idle' | 'connecting' | 'connected' | 'closed';

type Listener = (state: ConnectState) => void;
type StateListener = (raw: string) => void;

const SECRET_KEYS = new Set(['apiKey', 'password', 'token']);

const sanitizeLogValue = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map(sanitizeLogValue);
  if (!value || typeof value !== 'object') return value;
  return Object.fromEntries(
    Object.entries(value).map(([key, val]) => [
      key,
      SECRET_KEYS.has(key) && val ? '***' : sanitizeLogValue(val),
    ])
  );
};

const parseLogPayload = (payload: string) => {
  try {
    return sanitizeLogValue(JSON.parse(payload));
  } catch {
    return payload;
  }
};

const logMqttMessage = (direction: 'send' | 'recv', topic: string, payload: string) => {
  logger.log(`[MQTT][${direction}]`, topic, parseLogPayload(payload));
};

class ConsoleClient {
  private client: MqttJsClient | null = null;

  private state: ConnectState = 'idle';

  private brokerConnected = false;

  private stateListeners = new Set<Listener>();

  private stateTopicListeners = new Set<StateListener>();

  private currentStateTopic: string | null = null;

  private makeClientId() {
    return `${MQTT_CLIENT_ID_PREFIX}-console-${Date.now().toString(36)}-${Math.random()
      .toString(36)
      .slice(2, 8)}`;
  }

  getState() {
    return this.state;
  }

  isBrokerConnected() {
    return this.brokerConnected;
  }

  onState(l: Listener) {
    this.stateListeners.add(l);
    l(this.state);
    return () => this.stateListeners.delete(l);
  }

  onStateTopic(l: StateListener) {
    this.stateTopicListeners.add(l);
    return () => this.stateTopicListeners.delete(l);
  }

  private setState(s: ConnectState) {
    this.state = s;
    this.stateListeners.forEach((l) => l(s));
  }

  connect(opts: { brokerUrl?: string; roomId?: string }) {
    this.disconnect();
    const url = opts.brokerUrl || MQTT_BROKER_URL;
    this.setState('connecting');
    try {
      const client = mqtt.connect(url, {
        clientId: this.makeClientId(),
        username: MQTT_USERNAME || undefined,
        password: MQTT_PASSWORD || undefined,
        clean: true,
        reconnectPeriod: 3000,
        connectTimeout: 10000,
      });
      this.client = client;

      client.on('connect', () => {
        this.setState('connected');
        this.brokerConnected = true;
        if (opts.roomId) this.subscribeState(opts.roomId);
      });
      client.on('reconnect', () => {
        this.setState('connecting');
        this.brokerConnected = false;
      });
      client.on('close', () => {
        this.setState('closed');
        this.brokerConnected = false;
      });
      client.on('error', () => {
        this.setState('closed');
        this.brokerConnected = false;
      });
      client.on('message', (topic, payload) => {
        const raw = payload.toString('utf-8');
        logMqttMessage('recv', topic, raw);
        if (topic === this.currentStateTopic) this.stateTopicListeners.forEach((l) => l(raw));
      });
    } catch {
      this.setState('closed');
    }
  }

  disconnect() {
    if (this.client) {
      try {
        this.client.end(true);
      } catch {
        /* noop */
      }
      this.client = null;
    }
    this.currentStateTopic = null;
    this.brokerConnected = false;
    this.setState('idle');
  }

  subscribeState(roomId: string) {
    if (!this.client?.connected) return;
    const topic = MQTT_TOPIC_STATE(roomId);
    this.currentStateTopic = topic;
    this.client.subscribe(topic);
  }

  publishCmd(roomId: string, cmd: object): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.client?.connected) {
        reject(new Error('MQTT 未连接'));
        return;
      }
      if (!this.brokerConnected) {
        reject(new Error('broker 未连接'));
        return;
      }
      const topic = MQTT_TOPIC_CMD(roomId);
      const payload = JSON.stringify(cmd);
      logMqttMessage('send', topic, payload);
      this.client.publish(topic, payload, (err?: Error) => {
        if (err) reject(err);
        else resolve();
      });
    });
  }
}

export default new ConsoleClient();
